import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { DropdownTimezone, DropdownTimezoneItem, DropdownTimezoneSubmenu } from '../../../components/DropdownTimezone';
import { Agent, Queue, Timezone } from '../../../graphql/components';
import { getUTCHoursOffsetLabel } from '../../../utils/date';
import { formatTimezoneIdToName } from '../../../utils/timezoneFormatter';

export type TimezoneSwitcherProps = {
  agents: Record<string, Agent>;
  allTimezones: Timezone[];
  date: number;
  onTimezoneChange: (timezone: string) => void;
  queues: Record<string, Queue>;
  selectedAgentsIds: string[];
  scheduleTimezone: string;
  usedQueuesIds: string[];
  viewedTimezone: string;
};

export const TimezoneSwitcher: React.FC<TimezoneSwitcherProps> = ({
  agents,
  allTimezones,
  date,
  onTimezoneChange,
  queues,
  selectedAgentsIds,
  scheduleTimezone,
  usedQueuesIds,
  viewedTimezone,
}) => {
  const { locale, timezone: userTimezone } = useAuth();
  const { t } = useTranslation();

  const setTimezone = (timezone: string) => {
    if (timezone !== viewedTimezone) {
      onTimezoneChange(timezone);
    }
  };

  const userTimezoneSubtitle = `${formatTimezoneIdToName(userTimezone)} ${getUTCHoursOffsetLabel(
    new Date(date),
    userTimezone,
    locale,
    t
  )}`;

  const scheduleTimezoneSubtitle = `${formatTimezoneIdToName(scheduleTimezone)} ${getUTCHoursOffsetLabel(
    new Date(date),
    scheduleTimezone,
    locale,
    t
  )}`;

  const allTimezonesOptions = useMemo(() => allTimezones.map(({ zoneId }) => zoneId).sort(), [allTimezones]);
  const agentsTimezonesOptions = useMemo(
    () =>
      Array.from(new Set(selectedAgentsIds.map((agentId) => agents?.[agentId]?.timezone).filter((tz) => tz))).sort(),
    [agents, selectedAgentsIds]
  );
  const queuesTimezonesOptions = useMemo(
    () => Array.from(new Set(usedQueuesIds.map((queueId) => queues?.[queueId]?.timezone).filter((tz) => tz))).sort(),
    [queues, usedQueuesIds]
  );

  return (
    <DropdownTimezone activeTimezone={viewedTimezone} alignment="left" onTimezoneChange={setTimezone}>
      <DropdownTimezoneItem subtitle={scheduleTimezoneSubtitle} tz={scheduleTimezone}>
        {t('timezone.scheduleTimezone')}
      </DropdownTimezoneItem>
      <DropdownTimezoneItem subtitle={userTimezoneSubtitle} tz={userTimezone}>
        {t('timezone.myTimezone')}
      </DropdownTimezoneItem>
      <DropdownTimezoneSubmenu showSearch items={agentsTimezonesOptions}>
        <DropdownTimezoneItem subtitle={t('timezone.peoplesTimezoneInScheduleDescription')}>
          {t('timezone.peoplesTimezoneInSchedule')}
        </DropdownTimezoneItem>
      </DropdownTimezoneSubmenu>
      <DropdownTimezoneSubmenu showSearch items={queuesTimezonesOptions}>
        <DropdownTimezoneItem subtitle={t('timezone.queuesTimezonesInScheduleDescription')}>
          {t('timezone.queuesTimezonesInSchedule')}
        </DropdownTimezoneItem>
      </DropdownTimezoneSubmenu>
      <DropdownTimezoneSubmenu showSearch items={allTimezonesOptions} minHeightThreshold={400}>
        <DropdownTimezoneItem subtitle={t('timezone.allAvailableTimezones')}>
          {t('timezone.allTimezones')}
        </DropdownTimezoneItem>
      </DropdownTimezoneSubmenu>
    </DropdownTimezone>
  );
};
